import {IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {LockLine, MoreHorizontalLine} from '@dropbox/dig-icons/assets';
import {pendingDeleteCommentIdsAtom} from 'atoms/comment';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import cx from 'classnames';
import {Comment, CommentUpdate, DocumentThread} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import styles from 'components/DSYS/editor/plugins/Comment.module.css';
import {useDocumentCommentService} from 'components/DSYS/editor/plugins/comments/useThreads';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {EditSaveButtons} from 'components/shared/EditSaveButtons';
import {calculateTimeAgo} from 'components/shared/TimeAgo';
import {format, isThisYear, isToday, parseISO} from 'date-fns';
import {sanitizeCommentContent, serializeCommentEditorState} from 'helpers/commentContent';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {EditorState} from 'lexical';
import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {getCommentRenderState} from './commentRenderState';
import {ThreadButtons} from './ThreadButtons';

export const formatCommentTimestamp = (timestamp: string) => {
  const date = parseISO(timestamp);

  if (isToday(date)) {
    return calculateTimeAgo(timestamp);
  }

  return format(date, isThisYear(date) ? 'MMM d' : 'MMM d, yyyy');
};

const CommentOverflowButton = ({
  resolved,
  onEditClick,
  onDeleteClick,
}: {
  resolved: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      <Menu.Wrapper
        shouldPropagateClickOutsideMouseEvents
        className={isMenuOpen ? undefined : styles.actions}
        onToggle={({isOpen}) => setIsMenuOpen(isOpen)}
      >
        {({getContentProps, getTriggerProps}) => (
          <>
            <IconButton
              {...getTriggerProps()}
              variant="transparent"
              className={atoms({display: 'block'})}
              size="small"
            >
              <UIIcon src={MoreHorizontalLine} className={atoms({color: 'Text Subtle'})} />
            </IconButton>

            <Menu.Content {...getContentProps()} minWidth="200px" placement="bottom-start">
              <Menu.Segment>
                {!resolved && <Menu.ActionItem onClick={onEditClick}>{t('edit')}</Menu.ActionItem>}

                <Menu.ActionItem onClick={onDeleteClick}>{t('delete')}</Menu.ActionItem>
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>

      {/* <DeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('discard_comment')}
        subtitle={t('get_rid_of_comment_you_wrote')}
        isPending={isDeletePending}
        onDelete={() => deleteComment({commentId: comment.id})}
      /> */}
    </>
  );
};

const UserCommentRow = ({
  isFirstComment,
  comment,
  thread,
  updateComment,
  deleteComment,
  resolved,
  onResolveClick,
  onDeleteComment,
  enableCommentLinkCopy,
  commentRowCustomClass,
  hideOverflowButton,
  isPendingDelete = false,
  originalCommentedContent,
}: {
  isFirstComment: boolean;
  comment: Comment;
  thread: DocumentThread;
  updateComment: (args: {commentId: string; data: CommentUpdate}) => Promise<Comment>;
  deleteComment: (args: {commentId: string}) => Promise<unknown>;
  resolved: boolean;
  onResolveClick: (isResolved: boolean) => void;
  onDeleteComment?: () => void;
  enableCommentLinkCopy?: boolean;
  commentRowCustomClass?: string;
  hideOverflowButton?: boolean;
  isPendingDelete?: boolean;
  originalCommentedContent?: ReactNode;
}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const setSnackbar = useSetAtom(snackbarAtom);
  const [isEditing, setIsEditing] = useState(false);
  const [editorValue, setEditorValue] = useState<EditorState>();
  const serialized = serializeCommentEditorState(editorValue, comment.content);
  const editorValueString = sanitizeCommentContent(serialized);

  const editorRef = useRef<{
    focusEditor: () => void;
    clearEditor: (resetValue?: string) => void;
    parseEditorState: (state: string) => EditorState;
  }>(null);

  const handleCopyCommentLink = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('thread', thread.id);
      await navigator.clipboard.writeText(url.toString());
      setSnackbar({text: t('copied')});
    } catch {
      setSnackbar({text: t('error_copying_link')});
    }
  };

  const handleDelete = async () => {
    await deleteComment({commentId: comment.id});
    onDeleteComment?.();
  };

  // Initialize editor value with comment content whenever comment changes or editing starts
  const hasEditorInitialized = !!editorRef.current;
  useEffect(() => {
    if (!hasEditorInitialized) return;

    // Re-parse comment content when entering edit mode to ensure we have the latest saved content
    if (isEditing) {
      const editor = editorRef.current;

      // Use clearEditor to reset the editor with trimmed content
      // This ensures RichTextArea reloads the content from the value prop
      editor.clearEditor(comment.content);

      const parsedState = editor.parseEditorState(comment.content);
      setEditorValue(parsedState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment.id, hasEditorInitialized, isEditing]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isEditing) {
        updateComment({
          commentId: comment.id,
          data: {
            content: editorValueString.length ? editorValueString : comment.content,
            updated_at: new Date().toISOString(),
            is_deleted: !editorValueString.length,
          },
        }).finally(() => {
          setEditorValue(undefined);
          setIsEditing(false);
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isEditing, editorValueString, updateComment, comment.id, comment.content]);

  return (
    <Split
      gap="8"
      paddingX="16"
      paddingY="8"
      className={cx(styles.commentRow, commentRowCustomClass)}
      style={isPendingDelete ? {opacity: 0.6} : undefined}
    >
      <Split.Item width="hug">
        <Avatar size="small" user={comment.employee} />
      </Split.Item>

      <Split.Item width="fill">
        <Stack gap="4" style={{flexGrow: 1}}>
          <Box display="flex" flexDirection="row" style={{gap: 4}} alignItems="center">
            <Text color="faint" size="small" className={atoms({flexGrow: 1})}>
              <Text isBold>{comment.employee.name}</Text>{' '}
              {enableCommentLinkCopy ? (
                <Tooltip title={t('click_to_copy_comment_link')} openDelay={500}>
                  <button
                    type="button"
                    className={styles.commentTimestampButton}
                    onClick={handleCopyCommentLink}
                  >
                    {formatCommentTimestamp(comment.created_at)}
                  </button>
                </Tooltip>
              ) : (
                <Text color="faint" size="small" className={atoms({whiteSpace: 'nowrap'})}>
                  {formatCommentTimestamp(comment.created_at)}
                </Text>
              )}
            </Text>
            {!hideOverflowButton &&
              !isPendingDelete &&
              employee.user_id === comment.employee.user_id && (
                <CommentOverflowButton
                  resolved={resolved}
                  onEditClick={() => {
                    setTimeout(() => editorRef.current?.focusEditor(), 100);
                    setIsEditing(true);
                  }}
                  onDeleteClick={handleDelete}
                />
              )}
            {Boolean(thread.private_employees?.length || thread.private_teams?.length) && (
              <Tooltip
                title={t('comment_private_draft_written', {
                  notified: [
                    ...(thread.private_teams ?? []).map((e) => e.name),
                    ...(thread.private_employees ?? []).map((e) => e.name.split(' ')[0]),
                  ].join(', '),
                })}
              >
                <span>
                  <IconButton variant="borderless" size="small" disabled>
                    <UIIcon src={LockLine} className={atoms({color: 'Text Subtle'})} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isFirstComment && !isPendingDelete && (
              <ThreadButtons resolved={resolved} onResolveClick={onResolveClick} />
            )}
          </Box>

          {originalCommentedContent && <Box>{originalCommentedContent}</Box>}

          {isPendingDelete ? (
            <Text color="faint" size="small">
              {t('deleting')}
            </Text>
          ) : (
            <RichTextArea
              ref={editorRef}
              source="comments"
              value={comment.content}
              autoFocus
              editable={isEditing}
              editablePaddingX="0"
              disableBorder
              onChange={(value) => setEditorValue(value)}
              minHeight={40}
              hideToolbar
            />
          )}

          {isEditing && !isPendingDelete && (
            <EditSaveButtons
              border={false}
              size="small"
              cta={editorValueString.length ? t('save') : t('delete')}
              handleCancelClick={() => {
                editorRef.current?.clearEditor(comment.content);
                setEditorValue(undefined);
                setIsEditing(false);
              }}
              handleSaveClick={() => {
                if (editorValueString.length) {
                  updateComment({
                    commentId: comment.id,
                    data: {
                      content: editorValueString,
                      updated_at: new Date().toISOString(),
                      is_deleted: false,
                    },
                  }).finally(() => {
                    setEditorValue(undefined);
                    setIsEditing(false);
                  });
                } else {
                  handleDelete();
                }
              }}
              isLoading={false}
              disableSave={false}
            />
          )}
        </Stack>
      </Split.Item>
    </Split>
  );
};

type CommentBoxProps = {
  thread: DocumentThread;
  originalCommentedContent?: ReactNode;
  onDeleteComment?: () => void;
  onResolveClick: (isResolved: boolean) => void;
  maxHeight?: CSSProperties['maxHeight'];
  enableCommentLinkCopy?: boolean;
  commentRowCustomClass?: string;
  hideOverflowButton?: boolean;
  showFirstCommentOnOpen?: boolean;
  scrollToBottomRequestKey?: string | number | null;
};

export const CommentBox = ({
  thread,
  originalCommentedContent,
  onDeleteComment,
  onResolveClick,
  maxHeight,
  enableCommentLinkCopy = false,
  commentRowCustomClass,
  hideOverflowButton = false,
  showFirstCommentOnOpen = false,
  scrollToBottomRequestKey,
}: CommentBoxProps) => {
  const {updateComment, deleteComment} = useDocumentCommentService();
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastHandledScrollRequestKeyRef = useRef<string | number | null | undefined>(undefined);
  const pendingDeleteCommentIds = useAtomValue(pendingDeleteCommentIdsAtom);

  const {commentsToRender} = getCommentRenderState(thread.comments, pendingDeleteCommentIds);
  const resolvedMinHeight =
    typeof maxHeight === 'number' ? Math.min(100, Math.max(0, maxHeight)) : 100;

  useLayoutEffect(() => {
    if (showFirstCommentOnOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [showFirstCommentOnOpen, thread.id]);

  useEffect(() => {
    if (showFirstCommentOnOpen || !scrollRef.current) {
      return;
    }

    // Use requestAnimationFrame to ensure the DOM has updated with the new comment
    // before scrolling, otherwise the scroll happens before the new content is rendered.
    const frameId = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [showFirstCommentOnOpen, thread.comments]);

  useEffect(() => {
    if (
      scrollToBottomRequestKey == null ||
      scrollToBottomRequestKey === lastHandledScrollRequestKeyRef.current ||
      !scrollRef.current
    ) {
      return;
    }

    lastHandledScrollRequestKeyRef.current = scrollToBottomRequestKey;

    const frameId = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [scrollToBottomRequestKey]);

  return (
    <Box
      ref={scrollRef}
      paddingY="8"
      data-comment-box="true"
      overflowY="auto"
      display="flex"
      flexDirection="column"
      style={{
        minHeight: resolvedMinHeight,
        gap: '8',
        maxHeight: maxHeight ?? 600,
      }}
    >
      {commentsToRender.map((comment, index) => (
        <UserCommentRow
          isFirstComment={index === 0}
          originalCommentedContent={index === 0 ? originalCommentedContent : undefined}
          resolved={thread.is_resolved}
          onResolveClick={onResolveClick}
          key={comment.id}
          thread={thread}
          comment={comment}
          onDeleteComment={onDeleteComment}
          enableCommentLinkCopy={enableCommentLinkCopy}
          commentRowCustomClass={commentRowCustomClass}
          hideOverflowButton={hideOverflowButton}
          isPendingDelete={Boolean(pendingDeleteCommentIds[comment.id])}
          updateComment={updateComment}
          deleteComment={deleteComment}
        />
      ))}
    </Box>
  );
};

export const ThreadBox = ({
  thread,
  originalCommentedContent,
  onResolveClick,
  truncateLines,
  hideThreadButtonsOnCollapse,
  commentTextSize = 'small',
}: {
  thread: DocumentThread;
  originalCommentedContent?: ReactNode;
  onResolveClick: (isResolved: boolean) => void;
  truncateLines?: number;
  hideThreadButtonsOnCollapse?: boolean;
  commentTextSize?: 'small' | 'medium';
}) => {
  const pendingDeleteCommentIds = useAtomValue(pendingDeleteCommentIdsAtom);
  const {
    firstRenderableComment: comment,
    isDeletingLastComment,
    replyCount,
  } = getCommentRenderState(thread.comments, pendingDeleteCommentIds);

  return !comment ? null : (
    <Split gap="8" style={{opacity: thread?.is_resolved ? 0.5 : 1}}>
      <Split.Item width="hug" paddingTop="4">
        <Avatar size="small" user={comment.employee} />
      </Split.Item>

      <Split.Item width="fill">
        <Stack gap="4" style={{flexGrow: 1}}>
          <Box display="flex" flexDirection="row" style={{gap: 4}} alignItems="center">
            <Text color="faint" size="small" className={atoms({flexGrow: 1})}>
              <Text isBold>{comment.employee.name}</Text>{' '}
              {formatCommentTimestamp(comment.created_at)}
            </Text>
            {Boolean(thread.private_employees?.length || thread.private_teams?.length) && (
              <Tooltip
                title={t('comment_private_draft_written', {
                  notified: [
                    ...(thread.private_teams ?? []).map((e) => e.name),
                    ...(thread.private_employees ?? []).map((e) => e.name.split(' ')[0]),
                  ].join(', '),
                })}
              >
                <span>
                  <IconButton variant="borderless" size="small" disabled>
                    <UIIcon src={LockLine} className={atoms({color: 'Text Subtle'})} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!hideThreadButtonsOnCollapse && !isDeletingLastComment && (
              <ThreadButtons resolved={thread.is_resolved} onResolveClick={onResolveClick} />
            )}
          </Box>

          {originalCommentedContent && <Box>{originalCommentedContent}</Box>}
          {isDeletingLastComment ? (
            <Text color="faint" size="small">
              {t('deleting')}
            </Text>
          ) : (
            <RichTextArea
              textColor="inherit"
              textSize={commentTextSize}
              source="comments"
              value={comment.content}
              truncateLines={truncateLines}
            />
          )}

          {!isDeletingLastComment && replyCount > 0 && (
            <Box>
              <Text color="faint" size="small">
                {t('reply', {count: replyCount})}
              </Text>
            </Box>
          )}
        </Stack>
      </Split.Item>
    </Split>
  );
};
